import { styled, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useApiClientContext } from "../../app/ApiClientCtx";
import { ServerError } from "../../dataAccess/ServerError";
import { resendVerificationEmail } from "../../dataAccess/api/user";
import { FormFieldMessage } from "./FormFieldMessage";
import { ThemeButton } from "./ThemeComponents/ThemeButton";
import { ButtonProgress } from "./ButtonProgress";
import { useTranslation } from "../../helpers/i18n";
import { replaceBracesKey } from "../../helpers/formatter";
import { useConfiguration } from "../../dataAccess/api/configuration";
import { VerifyEmailSettings } from "../pages/VerifyEmail";
import {
  VERIFY_EMAIL_BUTTON_LABEL,
  VERIFY_EMAIL_LABEL,
  VERIFY_EMAIL_SUB_LABEL,
} from "../../../theme/defaultConstants";

interface Props {
  email?: string;
}

export const UserPopUpContainer = styled("form")(({ theme }) => ({
  padding: "1rem",
  display: "flex",
  alignItems: "flex-start",
  flexDirection: "column",
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(1),
  maxWidth: "100%",
  width: "100%",
  [theme.breakpoints.up("md")]: {
    width: 400,
  },
}));

export const UserPopUpTitle = styled(Typography)(({ theme }) => ({
  textAlign: "left",
  color: "black",
  marginBottom: "0.3rem",
  fontSize: theme.typography.h6.fontSize,
  textTransform: "uppercase",
}));

const StyledDescription = styled(Typography)(() => ({
  textAlign: "left",
  marginBottom: "1rem",
}));

const StyledButton = styled(ThemeButton)(() => ({
  alignSelf: "stretch",
}));

const VerifyEmailFormPopUp = ({ email }: Props) => {
  const { t } = useTranslation();
  const { APIV3 } = useApiClientContext();
  const { configuration } = useConfiguration();
  const [serverError, setServerError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const settings = useMemo(
    () =>
      configuration?.publishedTemplate?.properties?.pages.find(
        (page) => page?.id === "VerifyEmail",
      )?.settings as VerifyEmailSettings,
    [configuration],
  );
  const { buttonLabel, label, sublabel } = settings || {};

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    let response;
    if (email) {
      response = await resendVerificationEmail(APIV3, email).json();
    } else {
      setServerError(t("information_verification_no_email"));
      return;
    }

    if (response instanceof ServerError) {
      const errorResponse = response;
      setServerError(errorResponse.getMessage());
      setLoading(false);

      return;
    }
    setSuccessMessage(t("information_email_sent"));
    setLoading(false);
  };

  return (
    <UserPopUpContainer onSubmit={onSubmit}>
      <UserPopUpTitle variant="h1">
        {label || VERIFY_EMAIL_LABEL}
      </UserPopUpTitle>
      <StyledDescription>
        {replaceBracesKey(sublabel || VERIFY_EMAIL_SUB_LABEL, {
          key: "email",
          value: email,
        })}
      </StyledDescription>
      <StyledButton
        startIcon={loading ? <ButtonProgress /> : null}
        variant="contained"
        type="submit"
        disabled={loading}
      >
        {buttonLabel || VERIFY_EMAIL_BUTTON_LABEL}
      </StyledButton>
      {serverError && (
        <FormFieldMessage color="error">{serverError}</FormFieldMessage>
      )}
      {successMessage && (
        <FormFieldMessage color="success">{successMessage}</FormFieldMessage>
      )}
    </UserPopUpContainer>
  );
};

export default VerifyEmailFormPopUp;
